import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import { onBackWithinCard, onGoToNextCard } from '../Widget/actions';

import Overlay from '../Widget/Overlay';
import OverlayPartner from '../Widget/OverlayPartner';
import OverlayPublisher from '../Widget/OverlayPublisher';
import WidgetHeader from '../Widget/WidgetHeader';

import {
    Container,
    Body,
    Content,
    Sidebar,
    ContentContainer,
    FindRepLink,
    SidebarTitle,
    SidebarDescription,
    NotYourRepInfo
} from '../DefaultStyles/Details';

import { MultiImageBlock } from '../../components/Image/MultiImageBlock';

import CssTransition from '../../components/Transition/CssTransition';
import DetailsForm from '../../components/Email/DetailsForm';
import DetailsFormMultiRepStep1 from '../../components/Email/DetailsFormMultiRepStep1';
import FindLocalRepPopup from '../../components/LocalRep/FindLocalRepPopup';
import RepsNotContactable from '../../components/RepsNotContactable';
import ToasterAlert from '../../components/Donation/ToasterAlert';

import { DefaultRepImageURL } from '../../containers/DefaultStyles/Shared';

import { getActionCompletedTime } from '../../utils/actionTiming';
import { cacheFormData } from '../../utils/formCaching/actions';
import { onEmailComplete, onEmailMessageChange, continueToStep2 } from './actions';
import {
    relevantRepresentatives,
    joinEnglish
} from '../../utils/localRepresentative/parser';

import {
    onFindYourRep,
    hideFindRepPopup,
    loadLocalRep,
    onDeleteClick,
    onAddClick,
    onShowRepresentatives
} from '../LocalRep/actions';
import { CardTypes, RepresentativeDetails } from '../../utils/enums';
import { pictureUrl } from '../../utils/imageLoading';

import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { handleSkippingEndCard } from '../../utils/handleSkippingEndCard';
import { copyFor } from '../../copy/copy';

const EmailDetails = props => (
    <Container>
        <Overlay />
        <OverlayPublisher />
        <OverlayPartner transitionType="right-to-left" />
        <TransitionGroup>
            {props.emailSubmitError && (
                <CssTransition key="alert" in={props.emailSubmitError}>
                    <ToasterAlert content={props.emailSubmitError} />
                </CssTransition>
            )}
            {props.addressValidationError && (
                <CssTransition key="alert" in={props.addressValidationError}>
                    <ToasterAlert content={props.addressValidationError} />
                </CssTransition>
            )}
        </TransitionGroup>
        <Body>
            <WidgetHeader />
            {!props.emailableReps.length && props.useLocalRepresentative && (
                <RepsNotContactable />
            )}
            {((!!props.emailableReps.length && !props.includesUSSenators) ||
                !props.useLocalRepresentative) && (
                <Content>
                    <Sidebar>
                        <MultiImageBlock
                            isLocalRepLoading={props.isLocalRepLoading}
                            imgSources={props.recipientImages}
                        />
                        <SidebarTitle>
                            {copyFor('sidebar', 'title', 'email', undefined, {
                                recipientName: props.recipientName
                            })}
                        </SidebarTitle>
                        {props.localRepFound && props.useLocalRepresentative ? (
                            <NotYourRepInfo
                                isUnderImage={true}
                                isLocalRepLoading={props.isLocalRepLoading}
                            >
                                {copyFor('findReps', 'notYour', 'email')} <br />
                                <FindRepLink onClick={props.onFindYourRep}>
                                    {copyFor('findReps', 'clickToFind', 'email')}
                                </FindRepLink>
                            </NotYourRepInfo>
                        ) : (
                            <SidebarDescription>
                                {copyFor('sidebar', 'description', 'email')}
                            </SidebarDescription>
                        )}
                    </Sidebar>
                    <ContentContainer>
                        <DetailsForm
                            onSubmit={values => {
                                props.cacheFormData(values, props.actionIdentifier);
                                if (props.skipEndCard) {
                                    props.handleSkippingEndCard(
                                        props.nextAction,
                                        props.cards,
                                        props.onGoToNextCard
                                    );
                                }
                                props.onEmailComplete(
                                    props.apiRequestData,
                                    props.actionCompletedTime(),
                                    values,
                                    props.localRepData,
                                    props.isMultiActionsCard,
                                    props.activeAction,
                                    props.recipientMask,
                                    props.activeAction.representativeSearch,
                                    props.emailTopic,
                                    props.emailMessage,
                                    props.emailableReps,
                                    props.skipEndCard
                                );
                            }}
                            response={props.response}
                            onBackFromRepAction={() =>
                                props.onBackWithinCard(props.actionIdentifier, props.card)
                            }
                            isSubmitting={props.isSubmitting}
                            onFocus={onFocus(props.isMobileResolution)}
                            isEmailMessageChanged={props.isEmailMessageChanged}
                            onEmailMessageChange={props.onEmailMessageChange}
                            recipientName={props.recipientName}
                        />
                    </ContentContainer>
                </Content>
            )}
            {!!props.emailableReps.length && props.includesUSSenators && (
                <Content>
                    <ContentContainer>
                        {props.localRepFound && props.useLocalRepresentative && (
                            <NotYourRepInfo
                                isLocalRepLoading={props.isLocalRepLoading}
                                id="notYourRepInfo"
                            >
                                <FindRepLink onClick={props.onFindYourRep}>
                                    {copyFor('findReps', 'notYour', 'email')}
                                </FindRepLink>
                            </NotYourRepInfo>
                        )}
                        <DetailsFormMultiRepStep1
                            onSubmit={values => {
                                props.cacheFormData(values, props.actionIdentifier);
                                props.continueToStep2(values);
                            }}
                            onBackFromRepAction={() =>
                                props.onBackWithinCard(props.actionIdentifier, props.card)
                            }
                            onDeleteClick={data => props.onDeleteClick(data)}
                            onAddClick={data => props.onAddClick(data)}
                            onShowRepresentatives={() => props.onShowRepresentatives()}
                            isSubmitting={props.isSubmitting}
                            onFocus={onFocus(props.isMobileResolution, true)}
                            isEmailMessageChanged={props.isEmailMessageChanged}
                            onEmailMessageChange={props.onEmailMessageChange}
                            activeAction={props.activeAction}
                        />
                    </ContentContainer>
                </Content>
            )}
        </Body>
        {props.localRepFound && props.useLocalRepresentative && (
            <TransitionGroup>
                {props.showFindYouRepPopup && (
                    <CssTransition key="alert" in={props.showFindYouRepPopup}>
                        <FindLocalRepPopup
                            address={props.address}
                            hideFindRepPopup={props.hideFindRepPopup}
                            imgSources={props.recipientImages}
                            isDoubleCheckRepPopup={props.isDoubleCheckRepPopup}
                            isLocalRepLoading={props.isLocalRepLoading}
                            submitting={props.isLocalRepPopupLoading}
                            onSubmit={() =>
                                props.loadLocalRep(
                                    props.endUserLocation,
                                    encodeURI(props.address),
                                    props.representativeSearch,
                                    true
                                )
                            }
                        />
                    </CssTransition>
                )}
            </TransitionGroup>
        )}
    </Container>
);

const onFocus = (isMobileResolution, isMultiRep) => {
    if (!isMobileResolution) {
        setTimeout(() => {
            var textarea = document.getElementById('textarea');
            if (textarea) {
                textarea.addEventListener(
                    'focus',
                    function(event) {
                        document
                            .querySelectorAll('.row-wrapper-1')[0]
                            .classList.add('focused');
                        document
                            .querySelectorAll('.row-wrapper-2')[0]
                            .classList.add('focused');
                        document
                            .querySelectorAll('.row-wrapper-3')[0]
                            .classList.add('focused');

                        if (isMultiRep) {
                            document
                                .getElementById('notYourRepInfo')
                                .classList.add('focused');
                        }
                    },
                    true
                );

                textarea.addEventListener(
                    'blur',
                    function() {
                        document
                            .querySelectorAll('.row-wrapper-1')[0]
                            .classList.remove('focused');
                        document
                            .querySelectorAll('.row-wrapper-2')[0]
                            .classList.remove('focused');
                        document
                            .querySelectorAll('.row-wrapper-3')[0]
                            .classList.remove('focused');

                        if (isMultiRep) {
                            document
                                .getElementById('notYourRepInfo')
                                .classList.remove('focused');
                        }
                    },
                    true
                );
            }
        }, 50);
    }
};

EmailDetails.propTypes = {
    actionCompletedTime: PropTypes.func.isRequired,
    actionIdentifier: PropTypes.string.isRequired,
    activeAction: PropTypes.object.isRequired,
    address: PropTypes.string.isRequired,
    addressValidationError: PropTypes.any,
    apiRequestData: PropTypes.object.isRequired,
    cacheFormData: PropTypes.func.isRequired,
    card: PropTypes.object.isRequired,
    cards: PropTypes.array.isRequired,
    continueToStep2: PropTypes.func.isRequired,
    emailableReps: PropTypes.array.isRequired,
    emailMessage: PropTypes.string,
    emailSubmitError: PropTypes.string,
    emailTopic: PropTypes.string,
    endUserLocation: PropTypes.object,
    handleSkippingEndCard: PropTypes.func.isRequired,
    hideFindRepPopup: PropTypes.func.isRequired,
    includesUSSenators: PropTypes.bool.isRequired,
    isDoubleCheckRepPopup: PropTypes.bool.isRequired,
    isEmailMessageChanged: PropTypes.bool,
    isLocalRepLoading: PropTypes.bool,
    isLocalRepPopupLoading: PropTypes.bool.isRequired,
    isMobileResolution: PropTypes.bool,
    isMultiActionsCard: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    loadLocalRep: PropTypes.func.isRequired,
    localRepData: PropTypes.object,
    localRepFound: PropTypes.bool.isRequired,
    nextAction: PropTypes.object,
    onAddClick: PropTypes.func.isRequired,
    onBackWithinCard: PropTypes.func.isRequired,
    onDeleteClick: PropTypes.func.isRequired,
    onEmailComplete: PropTypes.func.isRequired,
    onEmailMessageChange: PropTypes.func.isRequired,
    onFindYourRep: PropTypes.func.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    onShowRepresentatives: PropTypes.func.isRequired,
    recipientImages: PropTypes.array,
    recipientMask: PropTypes.string,
    recipientName: PropTypes.string,
    representativeSearch: PropTypes.object,
    response: PropTypes.string,
    showFindYouRepPopup: PropTypes.bool.isRequired,
    skipEndCard: PropTypes.bool,
    useLocalRepresentative: PropTypes.bool
};

export default connect(
    state => {
        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        const reps = state.localRep.localRepData
            ? relevantRepresentatives(
                  state.widget.activeAction.representativeSearch,
                  state.localRep.localRepData.representative
              )
            : [];

        const emailableReps = reps.filter(rep => rep.canBeEmailed).slice(0, 3);
        const recipientImages = !!state.widget.activeAction.representativeSearch
            ? emailableReps.map(rep => rep.photoUrl || DefaultRepImageURL)
            : [pictureUrl(state) || DefaultRepImageURL];

        return {
            actionCompletedTime: getActionCompletedTime(state, 'email'),
            actionIdentifier: state.widget.activeAction.identifier,
            activeAction: state.widget.activeAction,
            address: state.form.google_autocomplete
                ? state.form.google_autocomplete.values.address
                : '',
            addressValidationError: state.widget.addressValidationError,
            apiRequestData: state.widget.apiRequestData,
            card: state.widget.cards[state.widget.activeAction.order - 1],
            cards: state.widget.cards,
            emailableReps,
            emailSubmitError: state.email.emailSubmitError
                ? state.email.emailSubmitError.message || state.email.emailSubmitError
                : state.email.emailSubmitError,
            endUserLocation: state.widget.endUserLocation,
            includesUSSenators: !!emailableReps.find(
                rep =>
                    rep.level === RepresentativeDetails.Level.Country &&
                    rep.role === RepresentativeDetails.Role.LegislatorUpperBody
            ),
            // isDoubleCheckRepPopup: state.localRep.isDoubleCheckRepPopup,
            isDoubleCheckRepPopup: false,
            isEmailMessageChanged: state.email.isEmailMessageChanged,
            isLocalRepLoading: state.localRep.isLocalRepLoading,
            isLocalRepPopupLoading: state.localRep.isLocalRepPopupLoading,
            isMobileResolution: state.widget.isMobileResolution,
            isMultiActionsCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.multipleActions,
            isStateLevel: !!emailableReps.find(
                rep => rep.level === RepresentativeDetails.Level.State
            ),
            isSubmitting: state.email.isSubmitting,
            localRepData: state.localRep.localRepData,
            localRepFound: state.localRep.localRepData.representative !== undefined,
            nextAction,
            emailTopic: state.email.topic || '',
            emailMessage: state.email.message || '',
            localRepSetByUserInput: state.localRep.localRepSetByUserInput,
            pictureUrl: pictureUrl(state),
            recipientMask: state.widget.activeAction.recipientMask,
            recipientName: !!state.widget.activeAction.representativeSearch
                ? joinEnglish(emailableReps.map(rep => rep.shortName))
                : state.widget.activeAction.recipientMask,
            recipientImages,
            representativeSearch: state.widget.maximumRepresentativeSearch,
            response: state.widget.activeAction.text,
            showFindYouRepPopup: state.localRep.showFindYouRepPopup,
            skipEndCard: state.widget.activeAction.skipEndCard && !!nextAction,
            thankYouText: state.widget.activeAction.thankYouText || '',
            thankYouTitle: state.widget.activeAction.thankYouTitle,
            useLocalRepresentative: !!state.widget.activeAction.representativeSearch
        };
    },
    {
        cacheFormData,
        continueToStep2,
        handleSkippingEndCard,
        hideFindRepPopup,
        loadLocalRep,
        onAddClick,
        onBackWithinCard,
        onDeleteClick,
        onEmailComplete,
        onEmailMessageChange,
        onFindYourRep,
        onGoToNextCard,
        onShowRepresentatives
    }
)(EmailDetails);
